import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ first_name:"", last_name:"", email:"", password:"", mailing_address:"", city:"", state:"TX", zip_code:"", phone:"", invite_code:"" });
  const [loading, setLoading] = useState(false);
  const [verifySent, setVerifySent] = useState(null); // { email } when verification email dispatched
  const [resending, setResending] = useState(false);

  const set = (k, v) => setF((s)=>({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) return toast.error("Password must be at least 8 characters");
    setLoading(true);
    try {
      const result = await signup(f);
      if (result && result.verification_required) {
        setVerifySent({ email: result.email });
        toast.success("Account created — check your email to confirm ownership");
        return;
      }
      toast.success("Account created");
      nav("/app/upload");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  const resend = async () => {
    if (!verifySent?.email) return;
    setResending(true);
    try {
      const { api } = await import("@/lib/apiClient");
      await api.post("/auth/resend-verification", { email: verifySent.email });
      toast.success("Verification email re-sent — check your inbox");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to resend");
    } finally { setResending(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center px-4 py-10">
      <div className="w-full max-w-2xl">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8" data-testid="signup-logo-link">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-purple-400 to-purple-600 grid place-items-center"><ShieldCheck size={18} className="text-white"/></div>
          <span className="font-semibold text-slate-900 text-lg">Informed <span className="text-purple-600">Accelerator</span></span>
        </Link>
        <Card className="glass rounded-2xl">
          <CardContent className="p-8">
            <div className="eyebrow">Create account</div>
            <h1 className="text-2xl font-semibold text-slate-900 mt-1">Set up your Informed profile</h1>
            <p className="text-sm text-slate-500 mt-1">Your information will auto-populate every dispute letter you generate.</p>
            {verifySent ? (
              <div className="mt-6 space-y-4" data-testid="signup-verify-panel">
                <div className="rounded-2xl border border-purple-200 bg-purple-50 p-5">
                  <div className="text-slate-900 font-semibold">Check your email</div>
                  <p className="text-sm text-slate-700 mt-2">We sent a confirmation link to <b>{verifySent.email}</b>. Click the button in that email within 24 hours to activate your account.</p>
                  <p className="text-xs text-slate-500 mt-2">Your account is inactive until both checks pass:</p>
                  <ul className="text-xs text-slate-600 mt-1 ml-4 list-disc space-y-0.5">
                    <li>Active Informed Accelerator (Skool) membership — <b>confirmed</b></li>
                    <li>Email ownership — <b>pending</b> (link in your inbox)</li>
                  </ul>
                </div>
                <div className="flex gap-2">
                  <Button onClick={resend} disabled={resending} variant="outline" data-testid="signup-resend-verify" className="rounded-xl">{resending ? "Sending…" : "Resend verification email"}</Button>
                  <Link to="/login"><Button variant="ghost" className="rounded-xl">Back to sign in</Button></Link>
                </div>
              </div>
            ) : null}
            {!verifySent && (
            <form onSubmit={submit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">First name</Label>
                <Input required value={f.first_name} onChange={(e)=>set("first_name", e.target.value)} data-testid="signup-firstname-input" className="mt-1 bg-slate-50 border-slate-200 text-slate-900" />
              </div>
              <div>
                <Label className="text-slate-700">Last name</Label>
                <Input required value={f.last_name} onChange={(e)=>set("last_name", e.target.value)} data-testid="signup-lastname-input" className="mt-1 bg-slate-50 border-slate-200 text-slate-900" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-slate-700">Email</Label>
                <Input type="email" required value={f.email} onChange={(e)=>set("email", e.target.value)} data-testid="signup-email-input" className="mt-1 bg-slate-50 border-slate-200 text-slate-900" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-slate-700">Password</Label>
                <Input type="password" required minLength={8} value={f.password} onChange={(e)=>set("password", e.target.value)} data-testid="signup-password-input" className="mt-1 bg-slate-50 border-slate-200 text-slate-900" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-slate-700">Mailing address</Label>
                <Input required value={f.mailing_address} onChange={(e)=>set("mailing_address", e.target.value)} data-testid="signup-address-input" className="mt-1 bg-slate-50 border-slate-200 text-slate-900" />
              </div>
              <div>
                <Label className="text-slate-700">City</Label>
                <Input required value={f.city} onChange={(e)=>set("city", e.target.value)} data-testid="signup-city-input" className="mt-1 bg-slate-50 border-slate-200 text-slate-900" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700">State</Label>
                  <select required value={f.state} onChange={(e)=>set("state", e.target.value)} data-testid="signup-state-select" className="mt-1 w-full rounded-md bg-slate-50 border border-slate-200 text-slate-900 px-3 h-10">
                    {STATES.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-slate-700">ZIP</Label>
                  <Input required value={f.zip_code} onChange={(e)=>set("zip_code", e.target.value)} data-testid="signup-zip-input" className="mt-1 bg-slate-50 border-slate-200 text-slate-900" />
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-slate-700">Phone (optional)</Label>
                <Input value={f.phone} onChange={(e)=>set("phone", e.target.value)} data-testid="signup-phone-input" className="mt-1 bg-slate-50 border-slate-200 text-slate-900" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-slate-700">Community invite code</Label>
                <Input required value={f.invite_code} onChange={(e)=>set("invite_code", e.target.value.toUpperCase())} data-testid="signup-invite-code-input" placeholder="e.g. INFORMED-LAUNCH" className="mt-1 bg-slate-50 border-slate-200 text-slate-900 uppercase tracking-widest font-mono" />
                <p className="text-xs text-slate-400 mt-1">Provided in the Skool community. Contact an admin if you don't have one.</p>
              </div>
              <div className="sm:col-span-2">
                <Button disabled={loading} type="submit" data-testid="signup-submit-button" className="btn-primary rounded-xl w-full py-6">
                  {loading ? "Creating..." : "Create account & start"}
                </Button>
              </div>
            </form>
            )}
            <p className="mt-4 text-xs text-slate-400 text-center">
              By continuing you agree that your report and information are used only to generate documents for your personal use.
            </p>
            <div className="mt-4 text-sm text-slate-500 text-center">
              Already registered? <Link to="/login" className="text-purple-600 hover:underline" data-testid="signup-to-login-link">Sign in</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
